<template>
  <v-card class="filter-panel" elevation="2">
    <v-card-title class="d-flex align-center">
      <v-icon class="me-2">mdi-filter</v-icon>
      Filtros de Análisis
      
      <v-spacer></v-spacer>
      
      <v-btn
        variant="text"
        size="small"
        @click="toggleExpanded"
        :icon="expanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"
      />
    </v-card-title>
    
    <v-expand-transition>
      <v-card-text v-show="expanded" class="pt-2">
        <v-row>
          <!-- Date Range Picker -->
          <v-col cols="12" md="3">
            <div class="filter-section">
              <h4 class="filter-title">📅 Rango de Fechas</h4>
              <DateRangePicker
                v-model="localDateRange"
                :presets="datePresets"
                @update="onDateRangeUpdate"
              />
            </div>
          </v-col>

          <!-- Quick Filters -->
          <v-col cols="12" md="6">
            <div class="filter-section">
              <h4 class="filter-title">🔍 Filtros Rápidos</h4>
              
              <v-row dense>
                <v-col
                  v-for="filter in availableFilters"
                  :key="filter.id"
                  cols="12"
                  sm="6"
                >
                  <FilterControl
                    :filter="filter"
                    :value="getFilterValue(filter.id)"
                    @update="updateFilter"
                  />
                </v-col>
              </v-row>
            </div>
          </v-col>

          <!-- Advanced Options -->
          <v-col cols="12" md="3">
            <div class="filter-section">
              <h4 class="filter-title">⚙️ Opciones Avanzadas</h4>
              
              <v-select
                v-model="comparisonPeriod"
                :items="comparisonOptions"
                label="Comparar con"
                variant="outlined"
                density="compact"
                class="mb-3"
              />
              
              <v-select
                v-model="granularity"
                :items="granularityOptions"
                label="Granularidad"
                variant="outlined"
                density="compact"
                class="mb-3"
              />
              
              <v-switch
                v-model="includeWeekends"
                label="Incluir fines de semana"
                color="primary"
                density="compact"
                class="mb-2"
              />
              
              <v-switch
                v-model="realTimeData"
                label="Datos en tiempo real"
                color="primary"
                density="compact"
              />
            </div>
          </v-col>
        </v-row>

        <!-- Active Filters Display -->
        <v-row v-if="hasActiveFilters" class="mt-4">
          <v-col cols="12">
            <div class="active-filters">
              <h4 class="filter-title mb-3">🏷️ Filtros Activos</h4>
              
              <div class="d-flex flex-wrap gap-2">
                <v-chip
                  v-for="filter in activeFilterChips"
                  :key="`${filter.id}-${filter.value}`"
                  color="primary"
                  variant="outlined"
                  closable
                  @click:close="removeFilter(filter.id, filter.value)"
                >
                  <v-icon start>{{ filter.icon }}</v-icon>
                  {{ filter.label }}: {{ filter.displayValue }}
                </v-chip>
                
                <v-chip
                  color="error"
                  variant="outlined"
                  @click="clearAllFilters"
                >
                  <v-icon start>mdi-close-circle</v-icon>
                  Limpiar Todo
                </v-chip>
              </div>
            </div>
          </v-col>
        </v-row>

        <!-- Action Buttons -->
        <v-row class="mt-4">
          <v-col cols="12">
            <div class="d-flex justify-end gap-2">
              <v-btn
                variant="outlined"
                @click="resetFilters"
                prepend-icon="mdi-refresh"
              >
                Restablecer
              </v-btn>
              
              <v-btn
                color="primary"
                @click="applyFilters"
                prepend-icon="mdi-check"
                :loading="applying"
              >
                Aplicar Filtros
              </v-btn>
              
              <v-btn
                color="success"
                variant="outlined"
                @click="saveAsPreset"
                prepend-icon="mdi-content-save"
              >
                Guardar Preset
              </v-btn>
            </div>
          </v-col>
        </v-row>

        <!-- Saved Presets -->
        <v-row v-if="savedPresets.length > 0" class="mt-2">
          <v-col cols="12">
            <v-divider class="mb-3"></v-divider>
            
            <h4 class="filter-title mb-3">💾 Presets Guardados</h4>
            
            <div class="d-flex flex-wrap gap-2">
              <v-chip
                v-for="preset in savedPresets"
                :key="preset.id"
                color="info"
                variant="outlined"
                @click="loadPreset(preset)"
              >
                <v-icon start>mdi-bookmark</v-icon>
                {{ preset.name }}
              </v-chip>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-expand-transition>
  </v-card>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import DateRangePicker from './DateRangePicker.vue'
import FilterControl from './FilterControl.vue'
import type { 
  AnalyticsFilter, 
  DateRange, 
  FilterPreset,
  FilterValue 
} from '@/types/analytics'

// 📝 Props & Emits
interface Props {
  filters: AnalyticsFilter[]
  dateRange: DateRange
  availableFilters: any[]
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'update:filters': [filters: AnalyticsFilter[]]
  'update:dateRange': [dateRange: DateRange]
  apply: []
  reset: []
}>()

// 🔄 Reactive State
const expanded = ref(true)
const applying = ref(false)
const localDateRange = ref<DateRange>(props.dateRange)
const localFilters = ref<AnalyticsFilter[]>([...props.filters])

// Advanced options
const comparisonPeriod = ref('previous_period')
const granularity = ref('day')
const includeWeekends = ref(true)
const realTimeData = ref(false)

// 📅 Date Presets
const datePresets = [
  {
    label: 'Últimas 24 horas',
    value: 'last_24h',
    range: () => ({
      start: new Date(Date.now() - 24 * 60 * 60 * 1000),
      end: new Date()
    })
  },
  {
    label: 'Últimos 7 días',
    value: 'last_7d',
    range: () => ({
      start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
      end: new Date()
    })
  },
  {
    label: 'Últimos 30 días',
    value: 'last_30d',
    range: () => ({
      start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
      end: new Date()
    })
  },
  {
    label: 'Este mes',
    value: 'this_month',
    range: () => {
      const now = new Date()
      return {
        start: new Date(now.getFullYear(), now.getMonth(), 1),
        end: new Date()
      }
    }
  },
  {
    label: 'Mes anterior',
    value: 'last_month',
    range: () => {
      const now = new Date()
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1)
      return {
        start: new Date(lastMonth.getFullYear(), lastMonth.getMonth(), 1),
        end: new Date(lastMonth.getFullYear(), lastMonth.getMonth() + 1, 0)
      }
    }
  }
]

// ⚙️ Options
const comparisonOptions = [
  { title: 'Período anterior', value: 'previous_period' },
  { title: 'Mismo período año anterior', value: 'year_over_year' },
  { title: 'Sin comparación', value: 'none' }
]

const granularityOptions = [
  { title: 'Hora', value: 'hour' },
  { title: 'Día', value: 'day' },
  { title: 'Semana', value: 'week' },
  { title: 'Mes', value: 'month' }
]

// 💾 Saved Presets (simulado - en producción vendría del store)
const savedPresets = ref<FilterPreset[]>([
  {
    id: '1',
    name: 'Vista Ejecutiva',
    filters: [],
    dateRange: datePresets[2].range(),
    settings: {
      comparisonPeriod: 'previous_period',
      granularity: 'day',
      includeWeekends: true
    }
  },
  {
    id: '2',
    name: 'Análisis Semanal',
    filters: [],
    dateRange: datePresets[1].range(),
    settings: {
      comparisonPeriod: 'year_over_year',
      granularity: 'day',
      includeWeekends: false
    }
  }
])

// 📊 Computed Properties
const hasActiveFilters = computed(() => {
  return localFilters.value.length > 0
})

const activeFilterChips = computed(() => {
  return localFilters.value.map(filter => {
    const config = props.availableFilters.find(f => f.id === filter.id)
    return {
      id: filter.id,
      value: filter.value,
      label: config?.label || filter.id,
      displayValue: Array.isArray(filter.value) 
        ? filter.value.join(', ') 
        : String(filter.value),
      icon: getFilterIcon(filter.id)
    }
  })
})

// 🔧 Helper Functions
const getFilterIcon = (filterId: string): string => {
  const iconMap: Record<string, string> = {
    user_type: 'mdi-account-group',
    region: 'mdi-map-marker',
    device: 'mdi-devices',
    status: 'mdi-check-circle',
    category: 'mdi-tag',
    source: 'mdi-source-branch'
  }
  return iconMap[filterId] || 'mdi-filter'
}

const getFilterValue = (filterId: string): FilterValue => {
  const filter = localFilters.value.find(f => f.id === filterId)
  return filter?.value || null
}

// 🔄 Methods
const toggleExpanded = () => {
  expanded.value = !expanded.value
}

const updateFilter = (filterId: string, value: FilterValue) => {
  const existingIndex = localFilters.value.findIndex(f => f.id === filterId)
  
  if (value === null || value === undefined || 
      (Array.isArray(value) && value.length === 0)) {
    // Remove filter
    if (existingIndex !== -1) {
      localFilters.value.splice(existingIndex, 1)
    }
  } else {
    // Add or update filter
    const filter: AnalyticsFilter = { id: filterId, value }
    
    if (existingIndex !== -1) {
      localFilters.value[existingIndex] = filter
    } else {
      localFilters.value.push(filter)
    }
  }
}

const removeFilter = (filterId: string, value: FilterValue) => {
  const index = localFilters.value.findIndex(
    f => f.id === filterId && f.value === value
  )
  if (index !== -1) {
    localFilters.value.splice(index, 1)
  }
}

const clearAllFilters = () => {
  localFilters.value = []
}

const onDateRangeUpdate = (newRange: DateRange) => {
  localDateRange.value = newRange
}

const applyFilters = async () => {
  applying.value = true
  
  try {
    // Update parent components
    emit('update:filters', [...localFilters.value])
    emit('update:dateRange', { ...localDateRange.value })
    emit('apply')
    
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 500))
    
  } finally {
    applying.value = false
  }
}

const resetFilters = () => {
  localFilters.value = []
  localDateRange.value = datePresets[1].range() // Default to last 7 days
  comparisonPeriod.value = 'previous_period'
  granularity.value = 'day'
  includeWeekends.value = true
  realTimeData.value = false
  
  emit('reset')
}

const saveAsPreset = () => {
  const name = prompt('Nombre del preset:')
  if (name) {
    const preset: FilterPreset = {
      id: Date.now().toString(),
      name,
      filters: [...localFilters.value],
      dateRange: { ...localDateRange.value },
      settings: {
        comparisonPeriod: comparisonPeriod.value,
        granularity: granularity.value,
        includeWeekends: includeWeekends.value
      }
    }
    
    savedPresets.value.push(preset)
    // In production, save to store/API
  }
}

const loadPreset = (preset: FilterPreset) => {
  localFilters.value = [...preset.filters]
  localDateRange.value = { ...preset.dateRange }
  
  if (preset.settings) {
    comparisonPeriod.value = preset.settings.comparisonPeriod
    granularity.value = preset.settings.granularity
    includeWeekends.value = preset.settings.includeWeekends
  }
  
  applyFilters()
}

// 👀 Watchers
watch(() => props.filters, (newFilters) => {
  localFilters.value = [...newFilters]
}, { deep: true })

watch(() => props.dateRange, (newRange) => {
  localDateRange.value = { ...newRange }
}, { deep: true })
</script>

<style scoped>
.filter-panel {
  border-radius: 12px !important;
  margin-bottom: 16px;
}

.filter-section {
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  height: 100%;
}

.filter-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #1976d2;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.active-filters {
  padding: 16px;
  background: #e3f2fd;
  border-radius: 8px;
  border-left: 4px solid #1976d2;
}

.gap-2 {
  gap: 8px;
}

@media (max-width: 960px) {
  .filter-section {
    padding: 12px;
    margin-bottom: 16px;
  }
  
  .filter-title {
    font-size: 0.85rem;
  }
}
</style>
